import { useCallback, useSyncExternalStore } from "react";

const STORAGE_KEY = "wiser_sidebar_pinned";

// Shared listeners so all hook instances stay in sync within a tab.
const listeners = new Set<() => void>();

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function getSnapshot(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(STORAGE_KEY);
}

function getServerSnapshot(): string | null {
  return null;
}

function notify() {
  for (const cb of listeners) cb();
}

/**
 * Persistent "sidebar pinned open" flag, backed by `localStorage`.
 * Mirrors the `useDefaultWorkspace` pattern: `useSyncExternalStore` keeps every
 * component instance in sync within the tab, and SSR returns `null` safely.
 *
 * Default value when nothing is stored: `false` (collapsed by default).
 */
export function useSidebarPinned() {
  const raw = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const pinned = raw === "true";

  const setPinned = useCallback((next: boolean) => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY, next ? "true" : "false");
    notify();
  }, []);

  const togglePinned = useCallback(() => {
    if (typeof window === "undefined") return;
    const current = window.localStorage.getItem(STORAGE_KEY) === "true";
    window.localStorage.setItem(STORAGE_KEY, current ? "false" : "true");
    notify();
  }, []);

  return { pinned, setPinned, togglePinned } as const;
}
